import React, { Fragment, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import requestApi from '../../../services/requestApi.service';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
  GridRowModes,
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from '@mui/x-data-grid';
import { confirmBoxWithImage, showToast } from '../../../Common/Appcommon';

function EditToolbar(props) {
  const { setRowData, setRowModesModel, rows } = props;

  const getMaxRowId = async () => {
    let lastRow = rows[rows.length - 1];
    let lastId = lastRow['id'];
    let newId = parseInt(lastId) + 1;
    return newId;
  }

  const handleClick = async () => {
    const id = await getMaxRowId();
    setRowData((oldRows) => [...oldRows, { id, propertyname: '', propertyvalue: '', isNew: true }]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'propertyname' },
    }));
  };

  return (
    <GridToolbarContainer>
      <button color="primary" className='btn btn-outline-success' onClick={handleClick}>
        <AddIcon /> Add record
      </button>
    </GridToolbarContainer>
  );
}

export default function GeneralSettings() {
  const [rows, setRowData] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [rowModesModel, setRowModesModel] = useState({});

  useEffect(() => {
    (async () => {
      await fetchGeneralSettingsData();
    })();
  }, []);

  const fetchGeneralSettingsData = async () => {
    let params = {
      "ENTITY": "General Settings",
      "OPERATION": 'GetAllMobileSettings'
    }
    let response = await requestApi.processServerRequest(params);
    let data = "";
    if (response.issuccess) {
      data = response.data;
      await processGeneralSettingsData(data);
    }
  }

  const processGeneralSettingsData = async (data) => {
    let tableRows = [];
    data.map((row, index) => {
      let id = row[0];
      let propertyname = row[1];
      let propertyvalue = row[2];
      let tmpRow = { "id": id, "propertyname": propertyname, "propertyvalue": propertyvalue };
      tableRows.push(tmpRow);
    });
    setRowData(tableRows);
  }

  const processSaveRow = async (updatedRow) => {
    let operation = updatedRow.isNew === undefined || !updatedRow.isNew ? "UPDATE" : "SAVE";
    if(updatedRow.propertyname === "" || updatedRow.propertyvalue === ""){
      showToast("Propertyname and Propertyvalue shouldn't be empty.",'top-right','error');
      await fetchGeneralSettingsData();
      return;
    }
    let params = {
      "ENTITY": "General Settings",
      "OPERATION": operation,
      "DATA" : updatedRow,
    }
    let response = await requestApi.processServerRequest(params);
    let data = "";
    if (response.issuccess) {
      data = response.data;
      await fetchGeneralSettingsData();
    }
    
  }

  const processDeleteRow = async (id) => {
    let params = {
      "ENTITY": "General Settings",
      "OPERATION": "DELETE",
      "id" : id,
    }
    let response = await requestApi.processServerRequest(params);
    let data = "";
    if (response.issuccess) {
      data = response.data;
      await fetchGeneralSettingsData(data);
    }
    
  }

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => async () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => async () => {
    let canDelete = await confirmBoxWithImage("Delete Settings","Are you sure you want to delete this settings?",'',"OK","Cancel","","",false,false,false);
    if(canDelete){
      setRowData(rows.filter((row) => row.id !== id));
      await processDeleteRow(id);
    }
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRowData(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = async (newRow) => {
    const updatedRow = newRow;//{ ...newRow, isNew: false };
    setRowData(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    await processSaveRow(updatedRow);
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 100 },
    {
      field: 'propertyname',
      headerName: 'Property Name',
      width: 350,
      editable: true,
      filterHeader: function TextFilterHeader(props) {
        return <GridStringFilter {...props} operator="contains" />;
      },
    },
    {
      field: 'propertyvalue',
      headerName: 'Property Value',
      width: 350,
      editable: true,
      filterHeader: function TextFilterHeader(props) {
        return <GridStringFilter {...props} operator="contains" />;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <Fragment>
      <Box
      sx={{
        height: 500,
        width: '100%',
        '& .actions': {
          color: 'text.secondary',
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        slots={{
          toolbar: EditToolbar,
          headerFilterMenu: null,
        }}
        disableColumnMenu
        slotProps={{
          toolbar: { setRowData, setRowModesModel, rows },
        }}
      />
    </Box>
    </Fragment>
  );
}